import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Mail, Linkedin, RefreshCw, Eye, MousePointerClick, Reply, AlertTriangle, MailX, Mic, UserPlus, MessageCircle, Send } from "lucide-react";
import LeadDrawer from "../components/LeadDrawer";

// Defensive normalizer — some legacy leads have `title` stored as an Apify
// search-filter object {title, departments, seniority}. Never throw on that.
const normalizeText = (v) => {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "object") return v.title || v.name || v.label || "";
  return String(v);
};

const STATUS_COLORS = {
  sent:     { bg: "hsl(var(--muted))",                  fg: "hsl(var(--muted-foreground))" },
  opened:   { bg: "hsl(220 70% 95%)",                   fg: "hsl(220 70% 35%)" },
  clicked:  { bg: "hsl(45 90% 92%)",                    fg: "hsl(35 80% 35%)" },
  replied:  { bg: "hsl(var(--success) / 0.18)",         fg: "hsl(var(--success))" },
  bounced:  { bg: "hsl(var(--destructive) / 0.12)",     fg: "hsl(var(--destructive))" },
  connect_request:  { bg: "hsl(220 70% 95%)",           fg: "hsl(220 70% 35%)" },
  connect_accepted: { bg: "hsl(var(--success) / 0.18)", fg: "hsl(var(--success))" },
  dm_sent:  { bg: "hsl(220 70% 95%)",                   fg: "hsl(220 70% 35%)" },
  voice_note_sent: { bg: "hsl(280 60% 92%)",            fg: "hsl(280 60% 35%)" },
  replied_li: { bg: "hsl(var(--success) / 0.18)",       fg: "hsl(var(--success))" },
};

const KIND_ICON = {
  email: Mail,
  linkedin: Linkedin,
  voice_note: Mic,
  voice_note_sent: Mic,
  linkedin_connect_request: UserPlus,
  linkedin_connect_accepted: UserPlus,
  linkedin_dm_sent: MessageCircle,
  linkedin_dm: MessageCircle,
  linkedin_replied: Reply,
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-AU", { timeZone: "Australia/Sydney", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function Outreach() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(7);
  const [channel, setChannel] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [drawerLead, setDrawerLead] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { days };
      if (channel !== "all") params.channel = channel;
      if (statusFilter !== "all") params.status = statusFilter;
      const { data: d } = await api.get("/outreach/activity", { params });
      setData(d);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load outreach activity");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [days, channel, statusFilter]);

  const openLead = async (leadId) => {
    try {
      const { data: lead } = await api.get(`/leads/${leadId}`);
      setDrawerLead(lead);
    } catch {
      toast.error("Could not open lead");
    }
  };

  const stats = data?.stats || {};
  const rows = data?.rows || [];

  return (
    <Layout>
      <div className="mb-6 flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1">Outreach activity</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter" style={{ fontWeight: 450 }}>
            What went out · who saw it
          </h1>
          <p className="text-sm text-muted-foreground mt-2">Every email and LinkedIn touch sent in the last {days} days, with engagement signals.</p>
        </div>
        <Button onClick={load} variant="outline" disabled={loading} data-testid="outreach-refresh" style={{ borderRadius: 2 }}>
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5} /> Refresh
        </Button>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        <Stat label="Emails sent" value={stats.emails_sent || 0} icon={Mail} testid="stat-emails-sent" />
        <Stat label="LinkedIn touches" value={stats.linkedin_touches || 0} icon={Linkedin} testid="stat-linkedin" />
        <Stat label="Opens" value={stats.opens || 0} sub={`${stats.open_rate || 0}%`} icon={Eye} testid="stat-opens" />
        <Stat label="Clicks" value={stats.clicks || 0} sub={`${stats.click_rate || 0}%`} icon={MousePointerClick} testid="stat-clicks" />
        <Stat label="Replies" value={stats.replies || 0} sub={`${stats.reply_rate || 0}%`} icon={Reply} testid="stat-replies" />
        <Stat label="Window" value={`${days}d`} icon={RefreshCw} testid="stat-window" />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-5 flex-wrap" data-testid="outreach-filters">
        <span className="overline mr-2">Filter</span>
        {[7, 14, 30, 90].map(d => (
          <button key={d} onClick={() => setDays(d)} data-testid={`filter-days-${d}`}
            className={`text-xs px-3 py-1.5 border font-mono ${days === d ? "bg-foreground text-background border-foreground" : "border-border bg-card hover:bg-accent"}`}
            style={{ borderRadius: 2 }}>{d}d</button>
        ))}
        <span className="text-muted-foreground mx-2">·</span>
        {[["all", "All channels"], ["email", "Email"], ["linkedin", "LinkedIn"]].map(([v, l]) => (
          <button key={v} onClick={() => setChannel(v)} data-testid={`filter-channel-${v}`}
            className={`text-xs px-3 py-1.5 border ${channel === v ? "bg-foreground text-background border-foreground" : "border-border bg-card hover:bg-accent"}`}
            style={{ borderRadius: 2 }}>{l}</button>
        ))}
        <span className="text-muted-foreground mx-2">·</span>
        {[["all", "All status"], ["sent", "Sent"], ["opened", "Opened"], ["clicked", "Clicked"], ["replied", "Replied"], ["bounced", "Bounced"]].map(([v, l]) => (
          <button key={v} onClick={() => setStatusFilter(v)} data-testid={`filter-status-${v}`}
            className={`text-xs px-3 py-1.5 border ${statusFilter === v ? "bg-foreground text-background border-foreground" : "border-border bg-card hover:bg-accent"}`}
            style={{ borderRadius: 2 }}>{l}</button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-card border border-border" style={{ borderRadius: 2 }} data-testid="outreach-table">
        {rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground">
            {loading ? "Loading…" : `No outreach activity in the last ${days} days for this filter.`}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left font-mono text-[10px] uppercase tracking-wider text-muted-foreground px-3 py-2.5">When</th>
                  <th className="text-left font-mono text-[10px] uppercase tracking-wider text-muted-foreground px-3 py-2.5">Channel</th>
                  <th className="text-left font-mono text-[10px] uppercase tracking-wider text-muted-foreground px-3 py-2.5">Lead</th>
                  <th className="text-left font-mono text-[10px] uppercase tracking-wider text-muted-foreground px-3 py-2.5">Subject / Action</th>
                  <th className="text-left font-mono text-[10px] uppercase tracking-wider text-muted-foreground px-3 py-2.5">Status</th>
                  <th className="text-left font-mono text-[10px] uppercase tracking-wider text-muted-foreground px-3 py-2.5">Via</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const ChannelIcon = KIND_ICON[r.kind] || (r.channel === "linkedin" ? Linkedin : Mail);
                  const sc = STATUS_COLORS[r.status] || STATUS_COLORS.sent;
                  return (
                    <tr key={`${r.id || r.kind}-${i}`} className="border-b border-border last:border-0 hover:bg-muted/20 cursor-pointer" onClick={() => openLead(r.lead_id)} data-testid={`outreach-row-${i}`}>
                      <td className="px-3 py-3 text-xs text-muted-foreground font-mono whitespace-nowrap">{fmtDate(r.at)}</td>
                      <td className="px-3 py-3"><ChannelIcon className="h-4 w-4" strokeWidth={1.5} /></td>
                      <td className="px-3 py-3">
                        <div className="font-medium">{r.lead_name}</div>
                        <div className="text-xs text-muted-foreground truncate max-w-[260px]">{[normalizeText(r.title), r.company].filter(Boolean).join(" · ")}</div>
                      </td>
                      <td className="px-3 py-3 max-w-[420px]">
                        <div className="truncate">{r.subject || "—"}</div>
                        {r.body && <div className="text-xs text-muted-foreground truncate">{r.body}</div>}
                        {r.step != null && <div className="text-[10px] font-mono text-muted-foreground mt-0.5">step {r.step}</div>}
                      </td>
                      <td className="px-3 py-3">
                        <span
                          className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5"
                          style={{ background: sc.bg, color: sc.fg, borderRadius: 2 }}
                          data-testid={`outreach-status-${r.status}`}
                        >
                          {r.status || "sent"}
                        </span>
                        {r.engagement && r.engagement.length > 0 && (
                          <div className="text-[10px] text-muted-foreground mt-1 font-mono">
                            {r.engagement.length} event{r.engagement.length !== 1 ? "s" : ""}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3 text-[10px] uppercase tracking-wider font-mono text-muted-foreground">{r.via}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {drawerLead && (
        <LeadDrawer lead={drawerLead} onClose={() => setDrawerLead(null)} onUpdated={load} defaultTab="timeline" />
      )}
    </Layout>
  );
}

function Stat({ label, value, sub, icon: Icon, testid }) {
  return (
    <div className="bg-card border border-border p-4" style={{ borderRadius: 2 }} data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <div className="overline">{label}</div>
        {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />}
      </div>
      <div className="font-serif text-3xl tracking-tight" style={{ fontWeight: 450 }}>
        {value}
        {sub && <span className="text-sm text-muted-foreground ml-1.5 font-sans">{sub}</span>}
      </div>
    </div>
  );
}
